import ViewAvailableTutors from "pages/CSM/ViewAvailableTutors/ViewAvailableTutors";
import { lazy, Suspense, useContext, useEffect } from "react";
import {
  Outlet,
  Route,
  Routes,
  useNavigate,
  useLocation,
} from "react-router-dom";
import { ROUTES } from "./routes";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import RequireAuth from "components/RequireAuth";
import { Toaster } from "react-hot-toast";
import { privateFetch } from "api/privateFetch";
import { AuthContext } from "context/AuthContext";
import { getCifNumberFromLocalStorage } from "utils/getToken";
import { useRef } from "react";
import FindTutor from "pages/FindTutor/FindTutor";
import OurPolicies from "pages/OurPolicies/OurPolicies";

const PublicCourseSearchResults = lazy(() =>
  import("pages/PublicCourseSearchResults")
);

// new page
const Career = lazy(() => import("./pages/Career/Career"));
const About = lazy(() => import("./pages/About/About"));
const Contact = lazy(() => import("./pages/Contact/Contact"));
const Planspricing = lazy(() => import("./pages/Planspricing/Planspricing"));
const Publicfaq = lazy(() => import("./pages/CommonFaq/Publicfaq"));
const NotFound = lazy(() => import("./pages/NotFound/NotFound"));

const Dashboard = lazy(() => import("./pages/dashboard"));
const Register = lazy(() => import("./pages/Register/Register"));
const Enquiry = lazy(() => import("./pages/Enquiry/Enquiry"));
const Login = lazy(() => import("./pages/Login"));
const OtpLogin = lazy(() => import("./pages/OtpLogin"));
const VerifyOtp = lazy(() => import("./pages/VerifyOtp"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const AddChild = lazy(() => import("./pages/AddChild/AddChild"));
const AddSubject = lazy(() => import("./pages/AddSubject/AddSubject"));
const StudentRecords = lazy(() =>
  import("./pages/StudentRecords/StudentRecords")
);
const ReferFriend = lazy(() => import("./pages/ReferFriend/ReferFriend"));
const ProgressReport = lazy(() =>
  import("./pages/ProgressReports/ProgressReport")
);
const AllSubjects = lazy(() => import("./pages/AllSubjects/AllSubjects"));
const SubjectReport = lazy(() => import("./pages/SubjectReport/SubjectReport"));
const MailUs = lazy(() => import("./pages/MailUs/MailUs"));
const NewChat = lazy(() => import("./pages/NewChat/NewChat"));
const ChangeTutor = lazy(() => import("./pages/ChangeTutor/ChangeTutor"));
const ReferralStatus = lazy(() =>
  import("./pages/ReferralStatus/ReferralStatus")
);
const InstallatoinManual = lazy(() =>
  import("./pages/InstallationManual/InstallationManual")
);
const SessionNotes = lazy(() => import("./pages/SessionNotes/SessionNotes"));
const StudentProfile = lazy(() =>
  import("./pages/StudentProfile/StudentProfile")
);
const ParentProfile = lazy(() => import("./pages/ParentProfile"));
const PasswordReset = lazy(() => import("./pages/PasswordReset/PasswordReset"));
const PasswordResetSuccess = lazy(() =>
  import("./pages/PasswordResetSuccess/PasswordResetSuccess")
);
const Billing = lazy(() => import("./pages/Billing/Billing"));
const UpdateBilling = lazy(() => import("./pages/UpdateBilling/UpdateBilling"));
const Settings = lazy(() => import("./pages/Settings/Settings"));
const ViewSchedule = lazy(() => import("./pages/ViewSchedule/ViewSchedule"));
const Faq = lazy(() => import("./pages/Faq/Faq"));
const Notifications = lazy(() => import("./pages/Notifications/Notifications"));
const Courses = lazy(() => import("./pages/Courses/Courses"));
const SearchResults = lazy(() => import("./pages/SearchResults/SearchResults"));

// tutor pages
const TutorDashboard = lazy(() => import("./pages/Tutor/Dashboard/Dashboard"));
const UpcomingSession = lazy(() =>
  import("./pages/Tutor/UpcomingSession/UpcomingSession")
);
const DailySchedule = lazy(() =>
  import("./pages/Tutor/DailySchedule/DailySchedule")
);
const RescheduleSession = lazy(() =>
  import("./pages/Tutor/Reschedule/Reschedule")
);
const OnlineLog = lazy(() => import("./pages/Tutor/OnlineLog/OnlineLog"));
const WeeklySchedule = lazy(() =>
  import("./pages/Tutor/WeeklySchedule/WeeklySchedule")
);
const TutorInbox = lazy(() => import("./pages/Tutor/Inbox/Inbox"));
const TutorNewChat = lazy(() => import("./pages/Tutor/NewChat/NewChat"));
const TutorProfile = lazy(() => import("./pages/Tutor/Profile/Profile"));
const TutorSettings = lazy(() => import("./pages/Tutor/Settings/Settings"));
const OnlineLogUpdate = lazy(() =>
  import("./pages/Tutor/OnlineLogUpdate/OnlineLogUpdate")
);
const TutorNotifications = lazy(() =>
  import("./pages/Tutor/Notifications/Notifications")
);
const TutorRegistration = lazy(() => import("./pages/Tutor/Register/Register"));

const TutorProgressReports = lazy(() =>
  import("./pages/Tutor/ProgressReport/ProgressReport")
);

const CsmStudentReport = lazy(() =>
  import("./pages/CSM/StudentReport/StudentReport")
);

// csm pages
const CsmDashboard = lazy(() => import("./pages/CSM/Dashboard/Dashboard"));
const CsmCourseRequests = lazy(() =>
  import("./pages/CSM/CourseRequests/CourseRequests")
);
const ViewProfile = lazy(() => import("./pages/CSM/ViewProfile/ViewProfile"));
const TutorStudentReport = lazy(() =>
  import("./pages/Tutor/StudentReport/StudentReport")
);
const CsmStudentContactDetails = lazy(() =>
  import("./pages/CSM/StudentContactDetails/StudentContactDetails")
);
const CsmViewAvailableTutors = lazy(() =>
  import("./pages/CSM/ViewAvailableTutors/ViewAvailableTutors")
);
const CsmContactParent = lazy(() =>
  import("./pages/CSM/ParentContactDetails/ParentContactDetails")
);
const CsmContactParentDetails = lazy(() =>
  import("./pages/CSM/ParentContactDetails/ShowDetails")
);
const CsmStudentList = lazy(() =>
  import("./pages/CSM/StudentList/StudentList")
);

const CsmUpcomingSessions = lazy(() =>
  import("./pages/CSM/UpcomingSessions/UpcomingSessions")
);

const CsmStudentDetails = lazy(() =>
  import("./pages/CSM/StudentDetails/StudentDetails")
);

const CsmSettings = lazy(() => import("./pages/CSM/Settings/Settings"));

const CsmCreateTimeSlot = lazy(() =>
  import("./pages/CSM/CreateTimeSlot/CreateTimeSlot")
);

const CsmAssignTimeSlot = lazy(() =>
  import("./pages/CSM/AssignTimeSlot/AssignTimeSlot")
);

const CsmAddSubject = lazy(() => import("./pages/CSM/AddSubject/AddSubject"));
const CsmAddSibling = lazy(() => import("./pages/CSM/AddSibling/AddSibling"));

const CsmReferFriend = lazy(() =>
  import("./pages/CSM/ReferFriend/ReferFriend")
);
const CsmReferralStatus = lazy(() =>
  import("./pages/CSM/ReferralStatus/ReferralStatus")
);

const CsmDisplayTimeSlot = lazy(() =>
  import("./pages/CSM/AssignTimeSlot/DisplayTimeSlot")
);
const CsmWeeklyShedule = lazy(() =>
  import("./pages/CSM/WeeklyShedule/Weeklyshedule")
);
const CsmNewChat = lazy(() => import("./pages/CSM/NewChat/NewChat"));

const CsmFeedback = lazy(() => import("./pages/CSM/Feedback/Feedback"));
const CsmDailySchedule = lazy(() =>
  import("./pages/CSM/DailySchedule/DailySchedule")
);
const CsmReschedule = lazy(() => import("./pages/CSM/Reschedule/Reschedule"));
const CsmSearchResults = lazy(() =>
  import("./pages/CSM/SearchResults/SearchResults")
);

const CsmListTutorList = lazy(() =>
  import("./pages/CSM/Listtutorsession/Listtutorsession")
);

const CsmStudentListForADay = lazy(() =>
  import("./pages/CSM/StudentListForADay/StudentListForADay")
);

// admin pages
const AdminManageEmployee = lazy(() =>
  import("./pages/Admin/ManageEmployee/ManageEmployee")
);
const AdminStudentReport = lazy(() =>
  import("./pages/Admin/StudentReport/StudentReport")
);

const AdminStudentRequests = lazy(() =>
  import("./pages/Admin/StudentRequests/StudentRequests")
);
const AdminDasboard = lazy(() => import("pages/Admin/Dashboard"));
const AdminManageCourse = lazy(() =>
  import("./pages/Admin/ManageCourse/ManageCourse")
);

const AdminAddCourse = lazy(() =>
  import("./pages/Admin/ManageCourse/AddCourse")
);

const AdminListTutorSession = lazy(() =>
  import("./pages/Admin/Listtutorsession/Listtutorsession")
);

const AddEmployee = lazy(() => import("./pages/Admin/AddEmployee/AddEmployee"));

const AdminCreateTimeSlot = lazy(() =>
  import("./pages/Admin/CreateTimeSlot/CreateTimeSlot")
);
const AdminCandidates = lazy(() => import("./pages/Admin/Candidates"));
const AdminProfile = lazy(() => import("pages/Admin/Profile"));
const AdminSettings = lazy(() => import("pages/Admin/Settings"));
const AdminPasswordResetSuccess = lazy(() =>
  import("pages/Admin/PasswordResetSuccess")
);

const StudentDashboard = lazy(() => import("pages/Student/Dashboard"));
const StudentPersonalProfile = lazy(() => import("pages/Student/Profile"));

// Common pages
const FeedbackDetails = lazy(() => import("./pages/FeedbackDetails"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  const navigate = useNavigate();
  const { logout, authState } = useContext(AuthContext);
  const location = useLocation();
  const locationRef = useRef(null);

  // const updateUserOnlineStatus = async () => {
  //   let id = getCifNumberFromLocalStorage();
  //   const res = await privateFetch.put(`/chat/${id}/status/${0}`);
  //   console.log("make user offline status res : ", res);
  // };

  useEffect(() => {
    const requestIntercept = privateFetch.interceptors.request.use(
      (config) => {
        if (!config.headers["Authorization"]) {
          config.headers["Authorization"] = `Bearer ${authState?.jwtToken}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    const responseIntercept = privateFetch.interceptors.response.use(
      (response) => response,
      async (error) => {
        const prevRequest = error?.config;
        if (
          error?.response?.status === 401 &&
          error?.response?.data?.message === "User session expired" &&
          !prevRequest?.sent
        ) {
          prevRequest.sent = true;
          logout();
          navigate(ROUTES.LOGIN, { replace: true });
          return privateFetch(prevRequest);
        }
        return Promise.reject(error);
      }
    );

    return () => {
      privateFetch.interceptors.request.eject(requestIntercept);
      privateFetch.interceptors.response.eject(responseIntercept);
    };
  }, []);

  // updates user online session when user exists out of chat page
  // useEffect(() => {
  //   let locationRegex = /(?=.*chat)/;
  //   if (locationRef.current?.match(locationRegex)) {
  //     updateUserOnlineStatus();
  //   }
  //   locationRef.current = location.pathname;
  // }, [location]);

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<p>loading....</p>}>
        <Routes>
          <Route path={ROUTES.HOME} element={<Outlet />}>
            {/* public routes */}
            <Route path={ROUTES.LOGIN} element={<Login />} />
            <Route path={ROUTES.OTPLOGIN} element={<OtpLogin />} />
            <Route path={ROUTES.REGISTER} element={<Register />} />
            <Route path={ROUTES.ENQUIRY} element={<Enquiry />} />
            <Route path={ROUTES.RESETPASSWORD} element={<ResetPassword />} />
            <Route path={ROUTES.FORGOTPASSWORD} element={<ForgotPassword />} />
            <Route path={ROUTES.VERIFYOTP} element={<VerifyOtp />} />
            <Route
              path={ROUTES.TUTOR_REGISTER}
              element={<TutorRegistration />}
            />
            <Route
              path={ROUTES.PUBLIC_COURSES_SEARCH_RESULTS}
              element={<PublicCourseSearchResults />}
            />

            {/* common pages */}
            <Route
              element={
                <RequireAuth
                  allowedRoles={[
                    "PARENT",
                    "TEACHER",
                    "CUSTOMERSUCCESSMANAGER",
                    "STUDENT",
                    "ADMINISTRATOR",
                    "QUALITYCONTROL",
                  ]}
                />
              }
            >
              <Route path={ROUTES.FAQ} element={<Faq />} />
              <Route
                path="/feedbackdetails/:caseNumber"
                element={<FeedbackDetails />}
              />
            </Route>
            {/* common pages end */}

            {/* common page */}
            <Route path="*" element={<NotFound />} />
            <Route path={ROUTES.CAREER} element={<Career />} />
            <Route path={ROUTES.ABOUT} element={<About />} />
            <Route path={ROUTES.CONTACT} element={<Contact />} />
            <Route path={ROUTES.PLANSPRICING} element={<Planspricing />} />
            <Route path={ROUTES.PUBLICFAQ} element={<Publicfaq />} />

            {/* protected routes */}
            {/* Parent pages */}
            <Route element={<RequireAuth allowedRoles={["PARENT"]} />}>
              <Route path={ROUTES.HOME} element={<Dashboard />} />
              <Route path={ROUTES.ADD_CHILD} element={<AddChild />} />
              {/* <Route path={ROUTES.FAQ} element={<Faq />} /> */}
              <Route path={ROUTES.ADD_SUBJECT} element={<AddSubject />} />
              <Route
                path={ROUTES.STUDENT_RECORDS}
                element={<StudentRecords />}
              />
              <Route
                path={ROUTES.PROGRESS_REPORT}
                element={<ProgressReport />}
              />
              <Route path={ROUTES.ALL_SUBJECTS} element={<AllSubjects />} />
              <Route path={ROUTES.SUBJECT_REPORT} element={<SubjectReport />} />
              <Route path={ROUTES.MAIL_US} element={<MailUs />} />
              <Route path={ROUTES.CHAT} element={<NewChat />} />
              {/* <Route path={ROUTES.NEW_CHAT} element={<NewChat />} /> */}
              <Route path={ROUTES.CHANGE_TUTOR} element={<ChangeTutor />} />
              <Route
                path={ROUTES.REFERRAL_STATUS}
                element={<ReferralStatus />}
              />
              <Route path={ROUTES.POLICIES} element={<OurPolicies />} />
              <Route path={ROUTES.REFER_FRIEND} element={<ReferFriend />} />
              <Route path={ROUTES.FIND_TUTOR} element={<FindTutor />} />
              <Route
                path={ROUTES.INSTALLATION}
                element={<InstallatoinManual />}
              />
              <Route path={ROUTES.SESSION_NOTES} element={<SessionNotes />} />
              <Route
                path={ROUTES.STUDENT_PROFILE}
                element={<StudentProfile />}
              />
              <Route path={ROUTES.PARENT_PROFILE} element={<ParentProfile />} />
              <Route path={ROUTES.PASSWORD_RESET} element={<PasswordReset />} />
              <Route
                path={ROUTES.PASSWORD_RESET_SUCCESS}
                element={<PasswordResetSuccess />}
              />
              <Route path={ROUTES.BILLING} element={<Billing />} />
              <Route path={ROUTES.UPDATE_BILLING} element={<UpdateBilling />} />
              <Route path={ROUTES.SETTINGS} element={<Settings />} />
              <Route path={ROUTES.VIEW_SCHEDULE} element={<ViewSchedule />} />
              <Route path={ROUTES.NOTIFICATIONS} element={<Notifications />} />
              <Route path={ROUTES.COURSES} element={<Courses />} />
              <Route path={ROUTES.SEARCH_RESULTS} element={<SearchResults />} />
            </Route>
            {/* Student pages */}
            <Route element={<RequireAuth allowedRoles={["STUDENT"]} />}>
              <Route
                path={ROUTES.STUDENT_DASHBOARD}
                element={<StudentDashboard />}
              />
              <Route
                path={ROUTES.STUDENT_PERSONAL_PROFILE}
                element={<StudentPersonalProfile />}
              />
            </Route>
            {/* tutor pages */}
            <Route element={<RequireAuth allowedRoles={["TEACHER"]} />}>
              <Route
                path={ROUTES.TUTOR_DASHBOARD}
                element={<UpcomingSession />}
              />
              <Route
                path={ROUTES.UPCOMING_SESSION}
                element={<UpcomingSession />}
              />
              <Route path={ROUTES.DAILY_SCHEDULE} element={<DailySchedule />} />
              <Route
                path={ROUTES.RESCHEDULE_SESSION}
                element={<RescheduleSession />}
              />
              <Route path={ROUTES.ONLINE_LOG} element={<OnlineLog />} />
              <Route
                path={ROUTES.WEEKLY_SCHEDULE}
                element={<WeeklySchedule />}
              />
              <Route path={ROUTES.TUTOR_INBOX} element={<TutorInbox />} />
              <Route path={ROUTES.TUTOR_CHAT} element={<TutorNewChat />} />
              {/* <Route path={ROUTES.TUTOR_NEW_CHAT} element={<TutorNewChat />} /> */}
              <Route path={ROUTES.TUTOR_PROFILE} element={<TutorProfile />} />
              <Route path={ROUTES.TUTOR_SETTINGS} element={<TutorSettings />} />
              <Route
                path={ROUTES.TUTOR_ONLINE_LOG_UPDATE}
                element={<OnlineLogUpdate />}
              />
              <Route
                path={ROUTES.TUTOR_STUDENT_REPORT}
                element={<TutorStudentReport />}
              />
              <Route
                path={ROUTES.TUTOR_NOTIFICATIONS}
                element={<TutorNotifications />}
              />
              <Route
                path={ROUTES.TUTUR_PROGRESS_REPORT}
                element={<TutorProgressReports />}
              />
            </Route>

            {/* CSM pages */}
            <Route
              element={
                <RequireAuth allowedRoles={["CUSTOMERSUCCESSMANAGER"]} />
              }
            >
              {/* csm pages */}
              <Route path={ROUTES.CSM_DASHBOARD} element={<CsmDashboard />} />
              <Route
                path={ROUTES.CSM_COURSE_REQUESTS}
                element={<CsmCourseRequests />}
              />
              <Route path={ROUTES.CSM_VIEW_PROFILE} element={<ViewProfile />} />
              <Route path={ROUTES.CSM_DASHBOARD} element={<CsmDashboard />} />
              <Route path={ROUTES.CSM_VIEW_PROFILE} element={<ViewProfile />} />
              <Route
                path={ROUTES.CSM_STUDENT_CONTACT_DETAILS}
                element={<CsmStudentContactDetails />}
              />

              <Route
                path={ROUTES.CSM_SEARCH_RESULTS}
                element={<CsmSearchResults />}
              />
              <Route
                path={ROUTES.CSM_VIEW_AVAILABLE_TUTORS}
                element={<CsmViewAvailableTutors />}
              />
              <Route
                path={ROUTES.CSM_PARENT_CONTACT}
                element={<CsmContactParent />}
              />
              <Route
                path={ROUTES.CSM_PARENT_CONTACT_DETAILS}
                element={<CsmContactParentDetails />}
              />
              <Route
                path={ROUTES.CSM_STUDENT_LIST}
                element={<CsmStudentList />}
              />
              <Route
                path={ROUTES.CSM_UPCOMING_SESSIONS}
                element={<CsmUpcomingSessions />}
              />

              <Route
                path={ROUTES.CSM_STUDENT_DETAILS}
                element={<CsmStudentDetails />}
              />
              <Route
                path={ROUTES.CSM_STUDENT_REPORT}
                element={<CsmStudentReport />}
              />
              <Route path={ROUTES.CSM_SETTINGS} element={<CsmSettings />} />
              <Route
                path={ROUTES.CSM_CREATE_TIME_SLOT}
                element={<CsmCreateTimeSlot />}
              />
              <Route
                path={ROUTES.CSM_ASSIGN_TIME_SLOT}
                element={<CsmAssignTimeSlot />}
              />
              <Route
                path={ROUTES.CSM_ADD_SUBJECT}
                element={<CsmAddSubject />}
              />
              <Route
                path={ROUTES.CSM_ADD_SIBLING}
                element={<CsmAddSibling />}
              />
              <Route
                path={ROUTES.CSM_DISPLAY_TIME_SLOT}
                element={<CsmDisplayTimeSlot />}
              />
              <Route path={ROUTES.CMS_CHAT} element={<CsmNewChat />} />
              {/* <Route path={ROUTES.CSM_NEW_CHAT} element={<CsmNewChat />} /> */}

              <Route path={ROUTES.CSM_FEEDBACK} element={<CsmFeedback />} />
              <Route
                path={ROUTES.CSM_SCHEDULE}
                element={<CsmDailySchedule />}
              />
              <Route
                path={ROUTES.CSM_RESCHEDULE_SESSION}
                element={<CsmReschedule />}
              />
              <Route
                path={ROUTES.CSM_LIST_TUTOR_SESSION}
                element={<CsmListTutorList />}
              />
              <Route
                path={ROUTES.CSM_LIST_MY_STUDENTS}
                element={<CsmStudentListForADay />}
              />
              <Route path={ROUTES.CSM_REFERRAL} element={<CsmReferFriend />} />
              <Route
                path={ROUTES.CSM_REFERRAL_STATUS}
                element={<CsmReferralStatus />}
              />
            </Route>

            {/* admin pages */}
            <Route
              element={
                <RequireAuth
                  allowedRoles={["ADMINISTRATOR", "QUALITYCONTROL"]}
                />
              }
            >
              <Route
                path={ROUTES.ADMIN_DASHBOARD}
                element={<AdminDasboard />}
              />
              <Route
                path={ROUTES.ADMIN_STUDENT_REPORT}
                element={<AdminStudentReport />}
              />
              <Route
                path={ROUTES.ADMIN_MANAGE_EMPLOYEE}
                element={<AdminManageEmployee />}
              />
              <Route
                path={ROUTES.ADMIN_STUDENT_REQUESTS}
                element={<AdminStudentRequests />}
              />
              <Route
                path={ROUTES.ADMIN_MANAGE_COURSE}
                element={<AdminManageCourse />}
              />
              <Route
                path={ROUTES.ADMIN_ADD_COURSE}
                element={<AdminAddCourse />}
              />
              <Route
                path={ROUTES.ADMIN_ADD_EMPLOYEE}
                element={<AddEmployee />}
              />

              <Route
                path={ROUTES.ADMIN_CREATE_TIME_SLOT}
                element={<AdminCreateTimeSlot />}
              />
              <Route
                path={ROUTES.ADMIN_CANDIDATES}
                element={<AdminCandidates />}
              />
              <Route
                path={ROUTES.ADMIN_LIST_TUTOR_SESSION}
                element={<AdminListTutorSession />}
              />
              <Route path={ROUTES.ADMIN_PROFILE} element={<AdminProfile />} />
              <Route path={ROUTES.ADMIN_SETTINGS} element={<AdminSettings />} />
              <Route
                path={ROUTES.ADMIN_PASSWORD_RESET_SUCCESS}
                element={<AdminPasswordResetSuccess />}
              />
            </Route>
          </Route>
        </Routes>
      </Suspense>
      <Toaster />
    </QueryClientProvider>
  );
}

export default App;
